/**
 * Trek pricing service
 *
 * Compta (https://compta.terralao.la) is the source of truth for trek GROUP
 * prices. The Enduro frontend fetches them via this lightweight RPC so that
 * editing a price in compta is reflected everywhere on the public site
 * (Home, BeyondEnduro, /trek listing, /trek/:slug, booking widget, chatbot)
 * without requiring a parallel write into the Enduro DB.
 *
 * Private prices stay in Enduro `activities.booking_info.private_price_usd`
 * — they are display-only metadata and never affect compta capacity math.
 *
 * Graceful fallback: if the compta API is unreachable or returns a row that
 * doesn't match an Enduro activity, components fall back to
 * `activity.price_usd` (the Enduro-stored price) so the site stays usable.
 */

const COMPTA_API_URL =
  import.meta.env.VITE_COMPTA_API_URL || 'https://compta.terralao.la/api'

export interface TrekPricingRow {
  source_id: string
  product_name: string
  group_price_usd: number
  max_capacity: number
}

/**
 * Fetch the latest trek pricing from compta.
 * Returns an empty array on any failure so the caller can fall back to Enduro DB values.
 */
export async function fetchTrekPricing(): Promise<TrekPricingRow[]> {
  try {
    const response = await fetch(`${COMPTA_API_URL}/rpc/get_trek_pricing`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({}),
    })
    if (!response.ok) {
      console.error('Trek pricing fetch failed:', response.status)
      return []
    }
    const rows = (await response.json()) as TrekPricingRow[]
    return rows.map((r) => ({
      ...r,
      group_price_usd: Number(r.group_price_usd),
    }))
  } catch (err) {
    console.error('Trek pricing fetch error:', err)
    return []
  }
}

/**
 * Resolve the effective group price for a trek activity, preferring compta.
 * Caller passes the Enduro activity row (must have `id` and `price_usd`) and
 * a map of compta rows keyed by source_id.
 */
export function resolveTrekGroupPrice(
  activity: { id: string; price_usd: number },
  comptaRows: TrekPricingRow[] | undefined | null
): number {
  if (!comptaRows || comptaRows.length === 0) return activity.price_usd
  const match = comptaRows.find((r) => r.source_id === activity.id)
  return match ? match.group_price_usd : activity.price_usd
}
